import React, { useState } from 'react';
import CommonHeader from '../../util_components/common_header';
import Button from '../../util_components/button';
import {
  DEFAULT_STAGING_API_HOST,
  STAGING_API_ENVIRONMENT_OPTIONS,
  getSelectedApiHost,
  setSelectedApiHost
} from '../../../utils/api_environment';
import './api_environment.css';

const isProduction = import.meta.env.VITE_APP_STAGE === 'production';

function ApiEnvironment() {
  const currentHost = getSelectedApiHost();
  const [selectedHost, setSelectedHost] = useState(currentHost);

  const hasChanges = !isProduction && selectedHost !== currentHost;
  const pendingHost = hasChanges ? selectedHost : currentHost;

  const applyEnvironment = () => {
    if (!hasChanges) {
      return;
    }

    setSelectedApiHost(selectedHost);
    window.location.reload();
  };

  return (
    <CommonHeader
      title="API Environment"
      subtitle="Switch the staging API host used across the CMS."
      show_back
    >
      <div className="api-environment-page">
        <div className="api-environment-summary">
          <div className="api-environment-summary-top">
            <div>
              <div className="api-environment-summary-label">Current endpoint</div>
              <div className="api-environment-summary-value">{currentHost}</div>
            </div>

            <div className="api-environment-summary-badges">
              <span className="api-environment-badge api-environment-badge-active">Active</span>
              {!isProduction ? (
                <span className="api-environment-badge">Shared Cookie</span>
              ) : (
                <span className="api-environment-badge">Production Locked</span>
              )}
            </div>
          </div>

          <div className="low-text api-environment-summary-copy">
            {isProduction
              ? 'Production is fixed and cannot be changed from this screen.'
              : 'Your selection is saved in a shared cookie and will be reused across myyogateacher subdomains after refresh so shared API helpers, chat modules, and other services stay on the same staging environment.'}
          </div>

          {!isProduction ? (
            <div className="api-environment-next-state">
              <span className="api-environment-next-state-label">Selected for next reload</span>
              <span className="api-environment-next-state-value">{pendingHost}</span>
            </div>
          ) : null}
        </div>

        <div className="api-environment-section-head">
          <div>
            <div className="api-environment-section-title">Available endpoints</div>
            <div className="low-text">Choose which staging API base URL the CMS should use.</div>
          </div>
        </div>

        <div className="api-environment-options">
          {STAGING_API_ENVIRONMENT_OPTIONS.map((option) => {
            const inputId = `api-environment-${option.value}`;
            const isSelected = selectedHost === option.value;
            const isCurrentHost = option.value === currentHost;

            return (
              <label
                htmlFor={inputId}
                className={`api-environment-card ${isSelected ? 'api-environment-card-active' : ''} ${
                  isProduction ? 'api-environment-row-disabled' : ''
                }`}
                key={option.value}
              >
                <div className="api-environment-card-select">
                  <span className="api-environment-radio-input">
                    <input
                      id={inputId}
                      type="radio"
                      name="api_environment"
                      value={option.value}
                      checked={selectedHost === option.value}
                      disabled={isProduction}
                      onChange={(event) => setSelectedHost(event.target.value)}
                    />
                  </span>
                </div>

                <div className="api-environment-card-body">
                  <div className="api-environment-option">
                    <span className="api-environment-option-title">{option.label}</span>

                    {option.value === DEFAULT_STAGING_API_HOST ? (
                      <span className="api-environment-badge">Default</span>
                    ) : null}

                    {isCurrentHost ? (
                      <span className="api-environment-badge api-environment-badge-active">
                        Active
                      </span>
                    ) : null}
                  </div>

                  <div className="api-environment-option-note">
                    {option.description}
                    {option.value === DEFAULT_STAGING_API_HOST ? (
                      <span className="api-environment-option-help">
                        This is the fallback used when no custom preference has been saved.
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="api-environment-card-side">
                  <div className="api-environment-card-side-label">
                    {isSelected ? 'Selected' : 'Click to select'}
                  </div>
                  <div className="api-environment-card-side-value">
                    {isCurrentHost ? 'In use now' : 'Available'}
                  </div>
                </div>
              </label>
            );
          })}
        </div>

        {!isProduction ? (
          <div className="api-environment-actions">
            <div className="api-environment-actions-copy low-text">
              {hasChanges
                ? 'You have a pending environment change. Apply it to reload the CMS with the new endpoint.'
                : 'No pending changes. Select a different endpoint to enable Apply And Reload.'}
            </div>
            <Button onClick={applyEnvironment} disabled={!hasChanges}>
              Apply And Reload
            </Button>
            <Button
              type="secondary"
              onClick={() => setSelectedHost(DEFAULT_STAGING_API_HOST)}
              disabled={selectedHost === DEFAULT_STAGING_API_HOST}
            >
              Reset To Default
            </Button>
          </div>
        ) : null}

        <div className="api-environment-footer low-text">
          Changes on this screen affect only API hosts for staging-style environments. Production
          continues to use the fixed production host.
        </div>
      </div>
    </CommonHeader>
  );
}

export default ApiEnvironment;
